
import React from 'react';
import { UseFormReturn } from "react-hook-form";
import { TerraceFormValues } from "../../../terrace-submission/types";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import TerraceOrientationSelect from '../../../terrace-submission/TerraceOrientationSelect';
import { useOrientationEstimation } from '@/hooks/useOrientationEstimation';
import { TerraceSubmission } from '@/types';

interface TerraceeFeaturesProps {
  form: UseFormReturn<TerraceFormValues>;
  disabled?: boolean;
  submission?: TerraceSubmission;
}

// Define valid orientation values to match the form schema
const VALID_ORIENTATIONS = ["N", "NE", "E", "SE", "S", "SW", "W", "NW", "unknown"] as const;
type ValidOrientation = typeof VALID_ORIENTATIONS[number];

const isValidOrientation = (value: string): value is ValidOrientation => {
  return VALID_ORIENTATIONS.includes(value as ValidOrientation);
};

const TerraceFeatures: React.FC<TerraceeFeaturesProps> = ({ form, disabled = false, submission }) => {
  const { estimateOrientation, isEstimating } = useOrientationEstimation();

  const handleEstimate = async () => {
    if (!submission) return;
    
    const result = await estimateOrientation({
      lat: submission.lat,
      lng: submission.lng,
      address: submission.address
    });
    
    if (result?.success && result.calculated_orientation) {
      // Validate that the calculated orientation is one of the expected values
      if (isValidOrientation(result.calculated_orientation)) {
        // Update the form with the estimated orientation
        form.setValue('orientation', result.calculated_orientation);
      } else {
        console.warn(`Invalid orientation received: ${result.calculated_orientation}, defaulting to 'unknown'`);
        form.setValue('orientation', 'unknown');
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Features</h3>
          
          <FormField
            control={form.control}
            name="hasFood"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={disabled}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className={disabled ? "text-muted-foreground" : ""}>
                    Serves Food
                  </FormLabel>
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="servesAlcohol"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={disabled}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className={disabled ? "text-muted-foreground" : ""}>
                    Serves Alcohol
                  </FormLabel>
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="petFriendly"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={disabled}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className={disabled ? "text-muted-foreground" : ""}>
                    Pet Friendly
                  </FormLabel>
                </div>
              </FormItem>
            )}
          />
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-medium">Location Details</h3>
          <TerraceOrientationSelect 
            form={form} 
            disabled={disabled}
            showEstimateButton={!!submission && !disabled}
            onEstimate={handleEstimate}
            isEstimating={isEstimating}
          />
        </div>
      </div>
    </div>
  );
};

export default TerraceFeatures;
